import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import PageBanner from "../components/PageBanner";
import Seo from "../components/Seo";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useZeoContent } from "../lib/ZeoContentContext";

export default function Contact() {
  const { business } = useZeoContent();
  const contactInfo = [
    { icon: Phone, label: "Telefon & WhatsApp", value: business.phone, href: `tel:${business.phoneRaw}` },
    { icon: Mail, label: "E-posta", value: "info@fridacicekcilik.com", href: "mailto:info@fridacicekcilik.com" },
    { icon: MapPin, label: "Adres", value: business.address },
    { icon: Clock, label: "Çalışma Saatleri", value: business.hours },
  ];

  const [form, setForm] = useState({ name: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      toast.success("Mesajınız alındı", {
        description: "Ekibimiz en kısa sürede size dönüş yapacak.",
      });
      setForm({ name: "", phone: "", subject: "", message: "" });
      setSubmitting(false);
    }, 600);
  };

  return (
    <div className="min-h-screen bg-white text-[#222222]" data-testid="contact-page">
      <Seo
        title="İletişim"
        description={`Frida Çiçekçilik ile iletişime geçin: ${business.phone}, ${business.address}. Sipariş, öneri ve sorularınız için 7/24 buradayız.`}
        path="/bize-ulasin"
      />
      <Header />

      <PageBanner
        eyebrow="Kurumsal"
        title="Bize Ulaşın"
        subtitle="Sorularınız, özel tasarım talepleriniz veya işbirliği önerileriniz için formu doldurun ya da doğrudan bizi arayın."
        breadcrumb={[{ label: "Bize Ulaşın" }]}
      />

      <section className="py-16 md:py-20 bg-white">
        <div className="frida-container grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-4 flex flex-col gap-4">
            {contactInfo.map((c) => (
              <div key={c.label} className="bg-[#F8F8F8] rounded-3xl p-6 flex items-start gap-4" data-testid={`contact-info-${c.label}`}>
                <div className="w-11 h-11 rounded-2xl bg-white border border-[#EAEAEA] flex items-center justify-center shrink-0">
                  <c.icon className="w-4 h-4 text-[#A85C34]" />
                </div>
                <div>
                  <div className="text-xs text-[#888888] mb-1">{c.label}</div>
                  {c.href ? (
                    <a href={c.href} className="font-medium text-[#222222] hover:text-[#A85C34] transition-colors">
                      {c.value}
                    </a>
                  ) : (
                    <div className="font-medium text-[#222222]">{c.value}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-8">
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-[#EAEAEA] rounded-3xl p-7 md:p-10"
              data-testid="contact-form"
            >
              <h3 className="font-serif text-2xl text-[#222222] mb-6">Mesaj Gönderin</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="text-sm font-medium text-[#222222] mb-2 block">Ad Soyad</label>
                  <Input
                    required
                    value={form.name}
                    onChange={handleChange("name")}
                    placeholder="Adınız Soyadınız"
                    className="h-12 rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#222222] mb-2 block">Telefon</label>
                  <Input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    placeholder="0532 000 00 00"
                    className="h-12 rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div className="mb-5">
                <label className="text-sm font-medium text-[#222222] mb-2 block">Konu</label>
                <Input
                  required
                  value={form.subject}
                  onChange={handleChange("subject")}
                  placeholder="Örn. Özel bir buket tasarımı hakkında bilgi almak istiyorum"
                  className="h-12 rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
                  data-testid="contact-subject-input"
                />
              </div>

              <div className="mb-7">
                <label className="text-sm font-medium text-[#222222] mb-2 block">Mesajınız</label>
                <Textarea
                  required
                  value={form.message}
                  onChange={handleChange("message")}
                  placeholder="Mesajınızı buraya yazın..."
                  className="min-h-[140px] rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full font-medium transition-colors active:scale-95 disabled:opacity-60"
                data-testid="contact-submit"
              >
                <Send className="w-4 h-4" />
                {submitting ? "Gönderiliyor..." : "Mesajı Gönder"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
}
